import React from 'react';
import { Button } from './ui/button';
import { CheckCircle, AlertTriangle, ArrowLeft, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Briefing content per dietary approach — placeholder texts (to be replaced by Coach Yuri)
const BRIEFINGS = {
  standard: {
    titleFr: 'Approche Standard (Flexible)',
    titleEn: 'Standard (Flexible) Approach',
    mechanismFr: 'L\'approche classique et equilibree. Aucune restriction stricte sur les groupes alimentaires. L\'objectif est de respecter tes macros (proteines, glucides, lipides) et ton budget calorique tout en gardant une flexibilite totale sur le choix de tes aliments.',
    mechanismEn: 'The classic, balanced approach. No strict restrictions on food groups. The goal is to hit your macros (protein, carbs, fats) and calorie budget while keeping total flexibility on food choices.',
    advantagesFr: ['Facile a maintenir socialement', 'Flexibilite totale', 'Ideal pour developper de bonnes habitudes a long terme sans frustration'],
    advantagesEn: ['Easy to maintain socially', 'Total flexibility', 'Ideal for building good long-term habits without frustration'],
    warningsFr: ['Exige de la discipline pour ne pas remplir son budget avec des aliments transformes', 'Demande un suivi rigoureux des portions'],
    warningsEn: ['Requires discipline not to fill your budget with processed foods', 'Requires rigorous portion tracking'],
  },
  keto: {
    titleFr: 'Approche Cetogene (Keto)',
    titleEn: 'Ketogenic (Keto) Approach',
    mechanismFr: 'Reduction drastique des glucides (moins de 5 a 10% des calories) au profit des bons gras et des proteines. Ton corps entre dans un etat de "cetose" et se transforme en machine a bruler tes propres reserves de graisses comme source d\'energie principale.',
    mechanismEn: 'Drastic reduction of carbs (less than 5-10% of calories) in favor of healthy fats and protein. Your body enters "ketosis" and becomes a machine that burns your own fat reserves as its primary energy source.',
    advantagesFr: ['Energie tres stable tout au long de la journee', 'Reduction massive de la faim et des fringales de sucre', 'Excellente efficacite pour la perte de gras'],
    advantagesEn: ['Very stable energy throughout the day', 'Massive reduction in hunger and sugar cravings', 'Excellent efficiency for fat loss'],
    warningsFr: ['Periode d\'adaptation possible les premiers jours (Keto flu)', 'Tres restrictif lors des sorties au restaurant', 'Elimine completement les sucres et presque tous les fruits'],
    warningsEn: ['Possible adaptation period in the first days (Keto flu)', 'Very restrictive when eating out', 'Completely eliminates sugars and almost all fruits'],
  },
  paleo: {
    titleFr: 'Approche Paleolithique (Paleo)',
    titleEn: 'Paleolithic (Paleo) Approach',
    mechanismFr: 'Un retour aux sources. Tu manges ce que nos ancetres chasseurs-cueilleurs trouvaient dans la nature : viandes, poissons, oeufs, legumes, fruits, et noix. On elimine tout ce qui est issu de l\'agriculture moderne ou de l\'industrie.',
    mechanismEn: 'A return to the roots. You eat what our hunter-gatherer ancestors found in nature: meats, fish, eggs, vegetables, fruits, and nuts. Everything from modern agriculture or industry is eliminated.',
    advantagesFr: ['Elimination naturelle des aliments ultra-transformes', 'Alimentation tres riche en vitamines et mineraux', 'Excellente pour la digestion et la reduction de l\'inflammation'],
    advantagesEn: ['Natural elimination of ultra-processed foods', 'Diet very rich in vitamins and minerals', 'Excellent for digestion and inflammation reduction'],
    warningsFr: ['Interdit les produits laitiers, les cereales (meme saines comme l\'avoine) et les legumineuses', 'Demande une bonne preparation de repas a l\'avance'],
    warningsEn: ['Prohibits dairy, grains (even healthy ones like oats) and legumes', 'Requires good meal prep in advance'],
  },
  carnivore: {
    titleFr: 'Approche Carnivore',
    titleEn: 'Carnivore Approach',
    mechanismFr: 'Le regime d\'elimination ultime. Tu consommes exclusivement des produits d\'origine animale : viandes (surtout rouges), abats, poissons, oeufs, et certains produits laitiers selon ta tolerance. Zero plante, zero fibre, zero glucide.',
    mechanismEn: 'The ultimate elimination diet. You consume exclusively animal products: meats (especially red), organ meats, fish, eggs, and some dairy based on tolerance. Zero plants, zero fiber, zero carbs.',
    advantagesFr: ['Proteines maximales pour la reconstruction musculaire', 'Digestion simplifiee pour ceux qui tolerent mal les fibres vegetales', 'Tres efficace pour identifier des intolerances alimentaires'],
    advantagesEn: ['Maximum protein for muscle rebuilding', 'Simplified digestion for those who tolerate plant fiber poorly', 'Very effective for identifying food intolerances'],
    warningsFr: ['Extremement restrictif', 'Demande un budget epicerie generalement plus eleve', 'La transition peut etre brutale pour le systeme digestif les premieres semaines'],
    warningsEn: ['Extremely restrictive', 'Generally requires a higher grocery budget', 'The transition can be brutal on the digestive system in the first weeks'],
  },
  vegetarian: {
    titleFr: 'Approche Vegetarienne',
    titleEn: 'Vegetarian Approach',
    mechanismFr: 'Basee principalement sur les vegetaux. Elimination de la viande et du poisson. Les proteines proviennent des legumineuses, du tofu, des noix, ou des oeufs et produits laitiers.',
    mechanismEn: 'Primarily plant-based. Elimination of meat and fish. Protein comes from legumes, tofu, nuts, or eggs and dairy products.',
    advantagesFr: ['Tres riche en fibres', 'Excellente approche pour la sante cardiovasculaire', 'Digestion souvent plus legere'],
    advantagesEn: ['Very rich in fiber', 'Excellent approach for cardiovascular health', 'Often lighter digestion'],
    warningsFr: ['Atteindre un quota eleve de proteines pour l\'entrainement demande une planification tres strategique', 'Risque de carences (Vitamine B12, Fer) si l\'alimentation n\'est pas bien geree'],
    warningsEn: ['Reaching a high protein quota for training requires very strategic planning', 'Risk of deficiencies (Vitamin B12, Iron) if diet is not well managed'],
  },
  vegan: {
    titleFr: 'Approche Vegetalienne (Vegan)',
    titleEn: 'Vegan Approach',
    mechanismFr: 'Basee totalement sur les vegetaux. Elimination de la viande, du poisson, des oeufs et de tous les produits laitiers. Les proteines proviennent exclusivement des legumineuses, du tofu, des noix et des cereales.',
    mechanismEn: 'Fully plant-based. Elimination of meat, fish, eggs and all dairy. Protein comes exclusively from legumes, tofu, nuts and grains.',
    advantagesFr: ['Tres riche en fibres', 'Excellente approche pour la sante cardiovasculaire', 'Digestion souvent plus legere'],
    advantagesEn: ['Very rich in fiber', 'Excellent approach for cardiovascular health', 'Often lighter digestion'],
    warningsFr: ['Atteindre un quota eleve de proteines pour l\'entrainement demande une planification tres strategique', 'Risque de carences (Vitamine B12, Fer) si l\'alimentation n\'est pas bien geree', 'Supplementation en B12 obligatoire'],
    warningsEn: ['Reaching a high protein quota for training requires very strategic planning', 'Risk of deficiencies (Vitamin B12, Iron) if diet is not well managed', 'B12 supplementation mandatory'],
  },
  pescatarian: {
    titleFr: 'Approche Pescetarienne',
    titleEn: 'Pescatarian Approach',
    mechanismFr: 'Tu elimines la viande mais conserves le poisson et les fruits de mer comme sources de proteines animales. Combine avec des oeufs, des produits laitiers, des legumineuses et des cereales pour un regime equilibre et riche en omega-3.',
    mechanismEn: 'You eliminate meat but keep fish and seafood as animal protein sources. Combined with eggs, dairy, legumes and grains for a balanced, omega-3 rich diet.',
    advantagesFr: ['Excellent apport en omega-3 (sante cardiaque et cerebrale)', 'Plus facile a suivre que le vegetarisme strict', 'Bonne variete de sources de proteines', 'Regime reconnu pour la longevite (diete mediterraneenne)'],
    advantagesEn: ['Excellent omega-3 intake (heart and brain health)', 'Easier to follow than strict vegetarianism', 'Good variety of protein sources', 'Diet recognized for longevity (Mediterranean)'],
    warningsFr: ['Attention aux metaux lourds dans certains poissons (mercure)', 'Cout variable selon la qualite du poisson', 'Peut manquer de fer heminique (viande rouge)'],
    warningsEn: ['Watch for heavy metals in some fish (mercury)', 'Variable cost depending on fish quality', 'May lack heme iron (red meat)'],
  },
  high_protein: {
    titleFr: 'Approche Haute Proteine',
    titleEn: 'High Protein Approach',
    mechanismFr: 'Priorite absolue aux proteines dans chaque repas. Tu vises 2g+ de proteines par kilo de poids corporel. Chaque repas est construit autour de la proteine, puis complete avec les glucides et lipides. Ideal pour la construction musculaire et la preservation en deficit.',
    mechanismEn: 'Absolute protein priority at every meal. You aim for 2g+ protein per kg of body weight. Every meal is built around protein, then completed with carbs and fats. Ideal for muscle building and preservation in deficit.',
    advantagesFr: ['Maximise la construction et la preservation musculaire', 'Effet thermique eleve (brule plus de calories a la digestion)', 'Sensation de satiete prolongee', 'Compatible avec presque tous les autres regimes'],
    advantagesEn: ['Maximizes muscle building and preservation', 'High thermic effect (burns more calories digesting)', 'Prolonged satiety feeling', 'Compatible with almost all other diets'],
    warningsFr: ['Cout eleve (les proteines de qualite coutent cher)', 'Necessite de planifier chaque repas autour des proteines', 'Les reins doivent etre en bonne sante (consulter si doute)'],
    warningsEn: ['Higher cost (quality protein is expensive)', 'Need to plan every meal around protein', 'Kidneys must be healthy (consult if in doubt)'],
  },
};

export default function DietaryBriefingModal({ approachId, language, onConfirm, onCancel }) {
  const fr = language === 'fr';
  const briefing = BRIEFINGS[approachId];
  if (!briefing) return null;

  const title = fr ? briefing.titleFr : briefing.titleEn;
  const mechanism = fr ? briefing.mechanismFr : briefing.mechanismEn;
  const advantages = fr ? briefing.advantagesFr : briefing.advantagesEn;
  const warnings = fr ? briefing.warningsFr : briefing.warningsEn;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="dietary-briefing-modal"
      >
        <motion.div
          initial={{ scale: 0.92, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl"
        >
          {/* Header */}
          <div className="p-5 pb-3 border-b border-border">
            <h3 className="font-heading text-lg uppercase tracking-tight text-foreground" data-testid="briefing-title">
              {title}
            </h3>
          </div>

          <div className="p-5 space-y-4">
            {/* Mechanism */}
            <div>
              <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground mb-1.5">
                {fr ? 'Le fonctionnement' : 'How it works'}
              </p>
              <p className="text-sm text-foreground/90 leading-relaxed" data-testid="briefing-mechanism">
                {mechanism}
              </p>
            </div>

            {/* Advantages */}
            <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <p className="text-[10px] font-heading uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                {fr ? 'Les avantages' : 'Advantages'}
              </p>
              <ul className="space-y-1.5" data-testid="briefing-advantages">
                {advantages.map((adv, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground/80">
                    <span className="text-emerald-400 mt-0.5 shrink-0">+</span>
                    {adv}
                  </li>
                ))}
              </ul>
            </div>

            {/* Warnings */}
            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
              <p className="text-[10px] font-heading uppercase tracking-wider text-amber-400 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {fr ? 'Points de vigilance' : 'Points to watch'}
              </p>
              <ul className="space-y-1.5" data-testid="briefing-warnings">
                {warnings.map((warn, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground/80">
                    <span className="text-amber-400 mt-0.5 shrink-0">!</span>
                    {warn}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="p-5 pt-3 border-t border-border space-y-2">
            <Button
              onClick={onConfirm}
              className="w-full h-11 bg-primary text-primary-foreground font-heading uppercase tracking-wider"
              data-testid="briefing-confirm-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              {fr ? 'Accepter la mission' : 'Accept the mission'}
            </Button>
            <Button
              variant="ghost"
              onClick={onCancel}
              className="w-full text-muted-foreground"
              data-testid="briefing-cancel-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {fr ? 'Retour aux options' : 'Back to options'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
